import { useState, useCallback } from 'react';

export interface ValidationRule {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp;
  email?: boolean;
  phone?: boolean;
  custom?: (value: string) => boolean;
  message: string;
}

export interface FieldValidation {
  [key: string]: ValidationRule[];
}

export interface FormErrors {
  [key: string]: string;
}

export interface TouchedFields {
  [key: string]: boolean;
}

export const useFormValidation = (validationRules: FieldValidation) => {
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<TouchedFields>({});

  const validateField = useCallback(
    (fieldName: string, value: string): string => {
      const rules = validationRules[fieldName];
      if (!rules) return '';

      for (const rule of rules) {
        // Required validation
        if (rule.required && !value.trim()) {
          return rule.message;
        }

        // Skip other validations if field is empty and not required
        if (!value.trim() && !rule.required) {
          continue;
        }

        // Min length validation
        if (rule.minLength && value.length < rule.minLength) {
          return rule.message;
        }

        // Max length validation
        if (rule.maxLength && value.length > rule.maxLength) {
          return rule.message;
        }

        // Email validation
        if (rule.email) {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(value)) {
            return rule.message;
          }
        }

        // Phone validation (Portuguese format)
        if (rule.phone) {
          const phoneRegex = /^(\+351|00351)?[1-9]\d{8}$/;
          if (!phoneRegex.test(value.replace(/\s/g, ''))) {
            return rule.message;
          }
        }

        // Pattern validation
        if (rule.pattern && !rule.pattern.test(value)) {
          return rule.message;
        }

        // Custom validation
        if (rule.custom && !rule.custom(value)) {
          return rule.message;
        }
      }

      return '';
    },
    [validationRules]
  );

  const validateAllFields = useCallback(
    (formData: { [key: string]: string }): boolean => {
      const newErrors: FormErrors = {};
      let isValid = true;

      Object.keys(validationRules).forEach((fieldName) => {
        const error = validateField(fieldName, formData[fieldName] || '');
        if (error) {
          newErrors[fieldName] = error;
          isValid = false;
        }
      });

      setErrors(newErrors);
      return isValid;
    },
    [validateField, validationRules]
  );

  const handleBlur = useCallback((fieldName: string, value: string) => {
    setTouched((prev) => ({ ...prev, [fieldName]: true }));
    const error = validateField(fieldName, value);
    setErrors((prev) => ({ ...prev, [fieldName]: error }));
  }, [validateField]);

  const handleChange = useCallback(
    (fieldName: string, value: string) => {
      // Only validate if field has been touched
      if (touched[fieldName]) {
        const error = validateField(fieldName, value);
        setErrors((prev) => ({ ...prev, [fieldName]: error }));
      }
    },
    [touched, validateField]
  );

  const resetValidation = useCallback(() => {
    setErrors({});
    setTouched({});
  }, []);

  return {
    errors,
    touched,
    validateField,
    validateAllFields,
    handleBlur,
    handleChange,
    resetValidation,
  };
};
